import { startCase } from 'lodash';
import { FC } from 'react';
import { Controller } from 'react-hook-form';

import ReactSelect from 'dashboard/components/ReactSelect';
import Form from 'dashboard/layouts/FormLayout/Form';
import FormGroup from 'dashboard/layouts/FormLayout/FormGroup';
import { ModalBody, ModalContainer, ModalHeader } from 'dashboard/layouts/ModalLayout';
import { TransactionType } from 'dashboard/models/transaction-type';
import { TriggerObjectTypes, TriggerType } from 'dashboard/models/trigger';
import { TriggerParams } from 'dashboard/redux/thunk-actions/workflow';
import Button from 'design-system/components/Button';
import FormField from 'design-system/components/FormField/FormField';
import Input from 'design-system/components/Input';
import Select, { Option } from 'design-system/components/Select';
import { Span } from 'design-system/components/Text';
import TextArea from 'design-system/components/TextArea';
import { EVENT_NAME_TO_EVENT_TYPE, EventName, EventType } from 'lib/constants/events';
import { FieldSchemaJson } from 'lib/fields';
import useFeatureFlag from 'lib/hooks/useFeatureFlag';
import useWorkflowTriggerableEvents from 'lib/hooks/useWorkflowTriggerableEvents';
import { ApiModel } from 'lib/interfaces/api';
import { useFormSecurely } from 'lib/security/reactHookForm';

interface CreateWorkflowModalProps {
  handleClose: () => void;
  hasWorkflowsEnhanced: boolean;
  onSubmit: (name: string, description: string, trigger: TriggerParams) => Promise<void>;
  transactionTypes: TransactionType[];
}

const CreateWorkflowModal: FC<CreateWorkflowModalProps> = ({
  handleClose,
  hasWorkflowsEnhanced,
  onSubmit,
  transactionTypes,
}) => {
  const basicWorkflowTriggerTypeOptions = [{ value: ApiModel.TriggerEvent, label: 'Event' }];
  const enhancedWorkflowTriggerTypeOptions = [
    ...basicWorkflowTriggerTypeOptions,
    { value: ApiModel.TriggerApi, label: 'API' },
    {
      value: ApiModel.TriggerSchedule,
      label: 'Scheduled Workflow',
    },
  ];

  const recurringTriggersEnabled = useFeatureFlag('dashboard:enable-workflows-recurring-triggers');

  if (recurringTriggersEnabled) {
    enhancedWorkflowTriggerTypeOptions.push({
      value: ApiModel.TriggerRecurring,
      label: 'Recurring',
    });
  }

  const triggerableEvents = useWorkflowTriggerableEvents();
  const eventOptions = triggerableEvents.map((e) => ({ label: e, value: e }));
  const scheduleTriggerTypeOptions = TriggerObjectTypes.map((tot) => ({
    value: tot,
    label: startCase(tot),
  }));
  const transactionTypeOptions = transactionTypes.map((tt) => ({
    value: tt.id,
    label: tt.attributes['name'],
  }));

  interface CreateWorkflowData {
    name: string;
    description: string;
    'trigger-type': TriggerType;
    'trigger-event': EventName;
    'trigger-object-type': ApiModel;
    'trigger-template': string;
  }

  const {
    control,
    formState: { errors, isSubmitting },
    handleSubmit,
    register,
    watch,
  } = useFormSecurely<CreateWorkflowData>({
    defaultValues: {
      name: '',
      description: '',
      'trigger-event': EventName.InquiryCompleted,
      'trigger-type': ApiModel.TriggerEvent,
      'trigger-object-type': ApiModel.Inquiry,
      'trigger-template': '',
    },
  });

  const watchedTriggerType = watch('trigger-type');
  const watchedTriggerEvent = watch('trigger-event');

  const submit = async (data: CreateWorkflowData) => {
    let extra;

    if (data['trigger-type'] === ApiModel.TriggerEvent) {
      extra = {
        'event-name': data['trigger-event'],
        rules: true,
        'template-filters': {} as Record<string, string[]>,
      };

      if (EVENT_NAME_TO_EVENT_TYPE[data['trigger-event']] === EventType.Transaction) {
        extra['template-filters']['transaction-type'] = [data['trigger-template']];
      }
    } else if (data['trigger-type'] === ApiModel.TriggerSchedule) {
      extra = { 'trigger-object-type': data['trigger-object-type'] };
    } else {
      extra = { 'param-schema': [] as FieldSchemaJson[] };
    }

    const trigger = {
      extra,
      type: data['trigger-type'],
    };

    await onSubmit(data.name, data.description, trigger as TriggerParams);
  };

  return (
    <ModalContainer>
      <ModalHeader onClose={handleClose}>New Workflow</ModalHeader>

      <ModalBody>
        <Form onSubmit={handleSubmit(submit)}>
          <FormGroup>
            <FormField
              label='Name'
              labelProps={{ htmlFor: 'create-workflow-modal__name' }}
              errorText={errors.name?.message}
            >
              <Input
                id='create-workflow-modal__name'
                hasError={!!errors.name}
                {...register('name', {
                  required: 'Please give your workflow a name',
                  maxLength: {
                    value: 255,
                    message: 'Workflow name must not exceed 255 characters',
                  },
                })}
              />
            </FormField>
            <FormField
              label='Description'
              labelProps={{ htmlFor: 'create-workflow-modal__description' }}
            >
              <TextArea
                id='create-workflow-modal__description'
                placeholder='Optional description for this workflow...'
                {...register('description')}
              />
            </FormField>
            <Controller
              control={control}
              name='trigger-type'
              render={({ field: { onChange, value } }) => {
                const helperText =
                  value === ApiModel.TriggerSchedule ? (
                    <>
                      This trigger type can only be triggered using the{' '}
                      <Span color='brand'>Schedule Workflow</Span> action
                    </>
                  ) : (
                    ''
                  );
                return (
                  <FormField
                    label='Trigger type'
                    labelProps={{ htmlFor: 'create-workflow-modal__trigger-type' }}
                    helperText={helperText}
                    helperProps={{ 'aria-label': 'Trigger type help text' }}
                  >
                    <Select
                      id='create-workflow-modal__trigger-type'
                      value={value}
                      onValueChange={onChange}
                    >
                      {(hasWorkflowsEnhanced
                        ? enhancedWorkflowTriggerTypeOptions
                        : basicWorkflowTriggerTypeOptions
                      ).map(({ label, value }) => (
                        <Option key={value} value={value}>
                          {label}
                        </Option>
                      ))}
                    </Select>
                  </FormField>
                );
              }}
            />
            {watchedTriggerType === ApiModel.TriggerEvent && (
              <Controller
                control={control}
                name='trigger-event'
                rules={{
                  required: {
                    value: watchedTriggerType === ApiModel.TriggerEvent,
                    message: 'Please select a trigger event',
                  },
                }}
                render={({ field: { onChange, value } }) => (
                  <FormField
                    label='Trigger event'
                    labelProps={{ htmlFor: 'create-workflow-modal__trigger-event-input' }}
                    errorText={errors['trigger-event']?.message}
                  >
                    <ReactSelect
                      id='create-workflow-modal__trigger-event'
                      value={value}
                      onChange={onChange}
                      options={eventOptions}
                      isSearchable={true}
                      portal={true}
                      hasError={!!errors['trigger-event']}
                    />
                  </FormField>
                )}
              />
            )}
            {watchedTriggerType === ApiModel.TriggerEvent &&
              EVENT_NAME_TO_EVENT_TYPE[watchedTriggerEvent] === EventType.Transaction && (
                <Controller
                  control={control}
                  name='trigger-template'
                  rules={{
                    required: {
                      value:
                        watchedTriggerType === ApiModel.TriggerEvent &&
                        EVENT_NAME_TO_EVENT_TYPE[watchedTriggerEvent] === EventType.Transaction,
                      message: 'Please select a transaction type',
                    },
                  }}
                  render={({ field: { onChange, value } }) => (
                    <FormField
                      label='Transaction type'
                      labelProps={{
                        htmlFor: 'create-workflow-modal__trigger-template-input',
                      }}
                      errorText={errors['trigger-template']?.message}
                    >
                      <ReactSelect
                        id='create-workflow-modal__trigger-template'
                        value={value}
                        onChange={onChange}
                        options={transactionTypeOptions}
                        isSearchable
                        portal
                        hasError={!!errors['trigger-template']}
                      />
                    </FormField>
                  )}
                />
              )}
            {watchedTriggerType === ApiModel.TriggerSchedule && (
              <Controller
                control={control}
                name='trigger-object-type'
                render={({ field: { onChange, value } }) => (
                  <FormField
                    label='Trigger object type'
                    labelProps={{
                      htmlFor: 'create-workflow-modal__trigger-object-type-input',
                    }}
                  >
                    <ReactSelect
                      id='create-workflow-modal__trigger-object-type'
                      value={value}
                      onChange={onChange}
                      options={scheduleTriggerTypeOptions}
                      isSearchable
                      portal
                    />
                  </FormField>
                )}
              />
            )}
          </FormGroup>

          <Button
            type='submit'
            width='auto'
            id='create-workflow-modal__btn-continue'
            isPending={isSubmitting}
          >
            Continue
          </Button>
        </Form>
      </ModalBody>
    </ModalContainer>
  );
};

export default CreateWorkflowModal;
